import React from "react";
import {Box, Button, Chip, Typography} from "@mui/material";
import PlaceIcon from "@mui/icons-material/PlaceOutlined";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import {useTranslation} from "react-i18next";
import {ClubEvent} from "../../data/sheet";
import {colors, displayFont} from "../../styling/GlobalTheme";
import {dateRange, monthShort} from "./format";
import Reveal from "./Reveal";

export function EventRow({e, past, index = 0}: { e: ClubEvent; past?: boolean; index?: number }) {
    const {t} = useTranslation();
    const multi = +e.end !== +e.start && e.end.getMonth() === e.start.getMonth();
    const external = /^https?:/.test(e.link);
    return (
        <Reveal delay={Math.min(index, 6) * 70}>
            <Box sx={{
                display: "grid", gridTemplateColumns: {xs: "70px 1fr", sm: "96px 96px 1fr auto"}, gap: {xs: 2, sm: 3},
                alignItems: "center", bgcolor: "#fff", borderRadius: 4, p: {xs: 2, sm: 2.5}, mb: 2,
                boxShadow: "0 10px 30px rgba(11,18,32,.06)", borderLeft: `6px solid ${e.featured ? colors.red : colors.glacier}`,
                opacity: past ? .55 : 1, transition: "transform .3s", "&:hover": {transform: "translateX(6px)"},
            }}>
                <Box sx={{textAlign: "center", lineHeight: 1}}>
                    <Typography sx={{fontFamily: displayFont, fontSize: {xs: "2.2rem", sm: "2.8rem"}, lineHeight: 1}}>
                        {e.start.getDate()}{multi && <Box component="span" sx={{fontSize: {xs: "1rem", sm: "1.3rem"}, color: "text.secondary"}}>–{e.end.getDate()}</Box>}
                    </Typography>
                    <Typography sx={{fontFamily: displayFont, fontSize: {xs: ".9rem", sm: "1.05rem"}, color: "text.secondary", textTransform: "uppercase"}}>
                        {monthShort(e.start)} {e.start.getFullYear()}
                    </Typography>
                </Box>
                <Box sx={{display: {xs: "none", sm: "block"}, width: 96, height: 72, borderRadius: 2.5, overflow: "hidden", bgcolor: colors.snow}}>
                    {e.image && <img src={e.image} alt="" loading="lazy" style={{width: "100%", height: "100%", objectFit: "cover"}}/>}
                </Box>
                <Box sx={{minWidth: 0}}>
                    <Box sx={{display: "flex", gap: .7, flexWrap: "wrap", mb: .5}}>
                        {e.type && <Chip size="small" label={t(e.type)} sx={{bgcolor: "#e4f7fa", color: colors.glacierDark, fontWeight: 700}}/>}
                        {e.featured && <Chip size="small" label="FIS" sx={{bgcolor: "#ffe1e4", color: colors.red, fontWeight: 800}}/>}
                    </Box>
                    <Typography sx={{fontWeight: 700, fontSize: "1.1rem"}}>{t(e.title)}</Typography>
                    <Typography sx={{color: "text.secondary", fontSize: ".92rem"}}>
                        {dateRange(e.start, e.end)}
                        {e.place && <><PlaceIcon sx={{fontSize: 16, verticalAlign: "-3px", ml: 1}}/> {e.place}</>}
                    </Typography>
                    {e.description && <Typography sx={{fontSize: ".95rem", mt: .5}}>{t(e.description)}</Typography>}
                </Box>
                {e.link ? (
                    <Button href={e.link} target={external ? "_blank" : undefined} rel="noopener" color="secondary"
                            variant="contained" endIcon={external ? <ArrowOutwardIcon/> : undefined}
                            sx={{gridColumn: {xs: "2", sm: "auto"}, justifySelf: "start", whiteSpace: "nowrap"}}>
                        {e.linkText ? t(e.linkText) : "Infos"}
                    </Button>
                ) : <span/>}
            </Box>
        </Reveal>
    );
}

export default function EventList({events, emptyText}: { events: ClubEvent[]; emptyText: string }) {
    if (!events.length) return <Typography sx={{color: "text.secondary", fontStyle: "italic"}}>{emptyText}</Typography>;
    return <>{events.map((e, i) => <EventRow key={i} e={e} index={i}/>)}</>;
}
